import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { cancelSubscription, fetchSubscriptionStatus } from '@maestra/core/store/slices/subscription';
import { estadoDaCota, formataBytes } from '@maestra/core/nucleo/cotaDeArmazenamento';

import { message } from '../../ui-carbon/padroes/avisos';
import { fmtBRL } from './useCheckoutDaAssinatura';

// O PAINEL DE QUEM JÁ ASSINA, sem tela. Cópia literal de `pages/Subscription/PainelDaAssinatura`:
// o que se paga, o que se tem e quanto se usa; a redução agendada à vista; em atraso, a ação é
// regularizar (e não aumentar o plano); cancelar mora aqui, com confirmação. Só leitura e um
// caminho: o upgrade tem tela própria.
//
// CANCELADA, com o PRO até o fim do período pago (QA de pagamentos, C3): sem cobrança, sem upgrade
// e sem cancelar de novo; o painel diz até quando o PRO segue.

/** Vencimento é DIA, não instante: sem `timeZone: UTC` a data recua um dia no Brasil. */
export const dataDoVencimento = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' });
  } catch {
    return '—';
  }
};

/** O fim do PRO é um instante (fim do dia em Brasília), não um vencimento: no fuso local. */
const dataDoFim = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '—';
  }
};

const COMO_SE_COBRA: Record<string, string> = {
  CREDIT_CARD: 'Cartão de crédito',
  PIX: 'PIX, com QR a cada ciclo',
  PIX_AUTOMATIC: 'Pix Automático (débito autorizado)',
};

export const usePainelDaAssinatura = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [cancelando, setCancelando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const {
    status, value, cycle, nextDueDate, pendingRenewal, gracePeriodEndsAt, billingType,
    includedProfiles, extraProfiles, extraStorageGb, profilesInQuota, legacyProfiles,
    scheduledExtraProfiles, scheduledExtraStorageGb, plan,
    storageQuotaBytes, storageUsedBytes, acessoAte, reducaoValeEm, valorAteAReducao, proporcionalPendente,
  } = useAppSelector((s) => s.subscription);

  const anual = cycle === 'YEARLY';
  const perfisContratados = includedProfiles + extraProfiles;
  const cota = storageQuotaBytes > 0 ? estadoDaCota(storageUsedBytes, storageQuotaBytes) : null;
  const gbContratados = Math.round(storageQuotaBytes / 1024 ** 3);
  const perfisAgendados = scheduledExtraProfiles !== null && scheduledExtraProfiles !== extraProfiles
    ? includedProfiles + scheduledExtraProfiles
    : null;
  const gbAgendados = scheduledExtraStorageGb !== null && scheduledExtraStorageGb !== extraStorageGb
    ? scheduledExtraStorageGb
    : null;
  const atrasada = status === 'overdue';
  const cancelada = status === 'active' && !!acessoAte;
  // Com redução agendada, `value` já é o preço da próxima cobrança; o de hoje é o do período em
  // curso (M10). E a redução tem data: o fim do período pago.
  const quandoReduz = reducaoValeEm ? dataDoFim(reducaoValeEm) : null;
  const precoDeHoje = valorAteAReducao ?? value;

  const cancelar = async () => {
    setCancelando(true);
    try {
      const r = await dispatch(cancelSubscription()).unwrap();
      await dispatch(fetchSubscriptionStatus());
      message.success(r?.acessoAte
        ? `Assinatura cancelada. Não há mais cobranças, e o PRO segue até ${dataDoFim(r.acessoAte)}.`
        : 'Assinatura cancelada.');
      setConfirmando(false);
    } catch (e) {
      message.error(typeof e === 'string' ? e : 'Não foi possível cancelar. Tente de novo.');
    } finally {
      setCancelando(false);
    }
  };

  return {
    // A tarja só aparece quando há o que comunicar ("Ativa" seria repetir o óbvio).
    alerta: cancelada ? 'Cancelada' : atrasada ? 'Pagamento em atraso' : pendingRenewal ? 'Renovação em aberto' : null,
    // Cancelada não é erro: a tarja é neutra.
    tomDoAlerta: cancelada ? 'gray' as const : 'red' as const,
    preco: precoDeHoje !== null ? fmtBRL(precoDeHoje) : '—',
    unidade: anual ? '/ano' : '/mês',
    notaDoPreco: cancelada
      ? `Sem novas cobranças. O PRO segue até ${dataDoFim(acessoAte)}, o fim do período já pago.`
      : valorAteAReducao !== null && value !== null && quandoReduz
        ? `Passa a ${fmtBRL(value)} em ${quandoReduz}, com a redução agendada.`
        : anual && value ? `Equivale a ${fmtBRL(value / 12)} por mês` : 'Cobrança recorrente, cancele quando quiser',
    perfis: {
      usados: profilesInQuota,
      contratados: perfisContratados,
      detalhe: (extraProfiles > 0
        ? `${includedProfiles} incluso${includedProfiles === 1 ? '' : 's'} no plano + ${extraProfiles} contratado${extraProfiles === 1 ? '' : 's'}.`
        : `${includedProfiles} incluso${includedProfiles === 1 ? '' : 's'} no plano.`)
        + (legacyProfiles > 0 ? ` Mais ${legacyProfiles} vitalício${legacyProfiles === 1 ? '' : 's'}, fora da cota.` : ''),
      agendado: perfisAgendados !== null ? `Passa a ${perfisAgendados} ${perfisAgendados === 1 ? 'perfil' : 'perfis'} ${quandoReduz ? `em ${quandoReduz}` : 'no fim do período pago'}.` : null,
    },
    espaco: {
      usado: formataBytes(storageUsedBytes),
      contratado: `${gbContratados} GB`,
      percentual: cota?.percentual ?? 0,
      cheio: cota?.nivel === 'cheio',
      detalhe: cota?.nivel === 'cheio'
        ? 'Espaço esgotado. Novos arquivos não serão enviados.'
        : cota?.perto ? 'Você está perto do limite do seu plano.' : 'Compartilhado entre todos os perfis da conta.',
      agendado: gbAgendados !== null ? `Passa a ${(plan?.includedStorageGb ?? 30) + gbAgendados} GB ${quandoReduz ? `em ${quandoReduz}` : 'no fim do período pago'}.` : null,
    },
    cobranca: [
      cancelada
        ? { rotulo: 'PRO até', valor: dataDoFim(acessoAte), aviso: false }
        : { rotulo: pendingRenewal ? 'Renovação em aberto, vence em' : 'Próxima cobrança', valor: dataDoVencimento(nextDueDate), aviso: pendingRenewal || atrasada },
      // A diferença proporcional de uma subida, ainda não paga (A6): vai junto da próxima cobrança.
      ...(!cancelada && proporcionalPendente > 0 ? [{ rotulo: 'Diferença proporcional, na próxima cobrança', valor: fmtBRL(proporcionalPendente), aviso: false }] : []),
      { rotulo: 'Forma de pagamento', valor: (billingType && COMO_SE_COBRA[billingType]) || 'PIX', aviso: false },
      { rotulo: 'Ciclo', valor: anual ? 'Anual' : 'Mensal', aviso: false },
      ...(atrasada && gracePeriodEndsAt ? [{ rotulo: 'Prazo de regularização', valor: dataDoVencimento(gracePeriodEndsAt), aviso: true }] : []),
    ],
    // Em atraso a ação principal é regularizar; oferecer upgrade a quem deve leva a um beco.
    // Regularizar é PAGAR (04/10/2026): a tela de pagamento abre o QR da cobrança vencida (PIX) ou
    // a fatura da Asaas (cartão, Pix Automático). Antes levava ao histórico, onde não se paga nada.
    // Cancelada não tem ação principal: não há cobrança a pagar nem plano a ajustar.
    principal: cancelada ? null
      : atrasada
        ? { rotulo: 'Regularizar pagamento', fazer: () => navigate('/pagamento') }
        : { rotulo: 'Upgrade', fazer: () => navigate('/planos/ajustar') },
    historico: () => navigate('/pagamentos'),
    cancelamento: {
      /** Já cancelada (o PRO segue até o fim do período): não há o que cancelar. */
      disponivel: !cancelada,
      aberto: confirmando,
      pedir: () => setConfirmando(true),
      fechar: () => setConfirmando(false),
      confirmar: () => { void cancelar(); },
      emCurso: cancelando,
    },
  };
};

export type DadosDoPainelDaAssinatura = ReturnType<typeof usePainelDaAssinatura>;
